import useI18n from 'Hooks/useI18n'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  homeSelectedContentTypesState,
  homeFeaturedTimeState,
  homeGenderState,
  homeNSFWState,
  homeSelectedTagIdsState,
  homeSortingState,
  homeTimeState,
} from 'Store/home'
import {
  mapGenderToApi,
  mapHomeSortingToApi,
  normalizeHomeSorting,
} from '@/constant/prefetch-home-sorting'
import {
  computeHomeImagePrefetchCount,
  HOME_GRID_STORAGE_PREFIX,
} from '@/constant/prefetch-home-size'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CharacterGetRes,
  CharacterGetResWithStory,
  HomeCharacterForYouReq,
  HomeCharacterListReq,
  HomeFeaturedTime,
  HomeGenderState,
  HomePopularTime,
  HomeSorting,
  HomeSortingType,
  HomeStoryReq,
  StoryGetRes,
} from 'Types/character'
import { Gender } from 'Types/types'
import useSWRInfinite from 'swr/infinite'
import {
  getCharacterForYouListPublic,
  getCharacterForYouListPublicKey,
  getCharacterListPublic,
  getCharacterListPublicKey,
  getStoryList,
  getStoryListKey,
} from 'Apis/character'
import {
  getSimulatorGameList,
  getSimulatorGameListKey,
  simulatorGameListUrl,
  type SimulatorGameListReq,
} from '@/apis/simulatorGame'
import {
  adaptSimulatorGameToHomeCard,
  type SimulatorGameHomeCard,
} from '@/components/simulator-game/adaptSimulatorGameToHomeCard'
import { message } from 'antd'
import sendLog from 'Utils/tracker'
import { userState } from 'Store/user'
import { useUpdateNsfw } from 'Hooks/useUpdateNsfw'
import { useMemoizedFn } from 'ahooks'
import { uuid } from '@/utils/func'
import {
  HOME_PERFORMANCE_MARKS,
  markPerformanceStage,
  runWithPerformanceStages,
} from '@/utils/firstScreenPerformance'
import {
  createHomeFirstScreenCacheMatch,
  mergeHomeFirstScreenCacheItems,
  readHomeFirstScreenCache,
  selectHomeFirstScreenCacheItems,
  writeHomeFirstScreenCache,
  type HomeFirstScreenCacheDevice,
  type HomeFirstScreenCacheMatch,
} from '@/constant/home-first-screen-cache'
import { BASE_API_URL } from '@/constant/api'

type Props = {
  LIMIT: number
  initialPageCount?: number
  device: HomeFirstScreenCacheDevice
}

type HomeListPage =
  | CharacterGetRes[]
  | StoryGetRes[]
  | CharacterGetResWithStory[]
  | SimulatorGameHomeCard[]

type HomeListItem = HomeListPage[number]

type HomeFirstPageKey = {
  endpoint?: unknown
  key?: unknown
  req?: unknown
}

const DEFAULT_LIST_ENDPOINT = `${BASE_API_URL}/character/get/public_list`
const FOR_YOU_LIST_ENDPOINT = `${BASE_API_URL}/recommend/recommend_feed/list`
const STORY_LIST_ENDPOINT = `${BASE_API_URL}/story/list`
const GAME_LIST_KEY = 'simulator-game-list'

const getLockedCacheItemCount = (device: HomeFirstScreenCacheDevice) => {
  try {
    const rawStoredGrid =
      typeof window === 'undefined'
        ? null
        : window.localStorage.getItem(
            `${HOME_GRID_STORAGE_PREFIX}home:${device}`
          )
    return computeHomeImagePrefetchCount(rawStoredGrid)
  } catch {
    return computeHomeImagePrefetchCount(null)
  }
}

const getForYouItemIdentity = (item: CharacterGetResWithStory) => {
  if (item.type === 'story') {
    return `story-${(item.data as StoryGetRes).story_id}`
  }
  if (item.type === 'character') {
    return `character-${(item.data as CharacterGetRes).character.character_id}`
  }
  return undefined
}

const resolveHomeListEndpoint = (key: HomeFirstPageKey): string => {
  if (typeof key.endpoint === 'string') {
    return key.endpoint.startsWith('/')
      ? `${BASE_API_URL}${key.endpoint}`
      : key.endpoint
  }
  if (key.key === GAME_LIST_KEY) return simulatorGameListUrl
  return ''
}

const useHomeCharacterList = ({
  LIMIT,
  initialPageCount = 1,
  device,
}: Props) => {
  const { lang } = useI18n()
  const user = useRecoilValue(userState)
  const userId = user?.user_id ? String(user.user_id) : ''
  const [sorting, setSorting] = useRecoilState(homeSortingState)
  const [sex, setSex] = useRecoilState(homeGenderState)
  const nsfw = useRecoilValue(homeNSFWState)
  const { updateNsfw } = useUpdateNsfw()
  const [time, setTime] = useRecoilState(homeTimeState)
  const [featuredTime, setFeaturedTime] = useRecoilState(homeFeaturedTimeState)
  const selectedTagIds = useRecoilValue(homeSelectedTagIdsState)
  const selectedContentTypes = useRecoilValue(homeSelectedContentTypesState)
  const activeCacheMatchRef = useRef<HomeFirstScreenCacheMatch>()
  const [lockedCacheItemCount] = useState(() => getLockedCacheItemCount(device))

  // 首屏请求成功后在浏览器空闲期落盘，避免 JSON stringify/localStorage 同步 I/O
  // 阻塞列表响应提交。写入前再次校验当前请求，防止快速切筛选时旧响应覆盖新快照。
  const persistFirstPage = useCallback(
    (endpoint: string, req: { page: number }, items: HomeListPage) => {
      if (req.page !== 0 || items.length === 0) return
      const match = createHomeFirstScreenCacheMatch({
        endpoint,
        body: req,
        userId,
        device,
      })
      if (activeCacheMatchRef.current?.requestKey !== match.requestKey) return

      const write = () => {
        const activeMatch = activeCacheMatchRef.current
        if (
          activeMatch?.requestKey !== match.requestKey ||
          activeMatch.storageKey !== match.storageKey
        ) {
          return
        }
        writeHomeFirstScreenCache<HomeListItem>(
          match,
          selectHomeFirstScreenCacheItems<HomeListItem>(
            items as readonly HomeListItem[],
            lockedCacheItemCount
          )
        )
      }

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(write, { timeout: 2000 })
      } else {
        window.setTimeout(write, 0)
      }
    },
    [device, lockedCacheItemCount, userId]
  )

  // For You 的 session_id，用 state 存储，变化时触发重新请求
  const [forYouSessionId, setForYouSessionId] = useState<string>(uuid)

  // 记录上一次的 sorting 值，用于判断是否从其他 Tab 切换到 For You
  const prevSortingRef = useRef<string>(sorting)
  // 将 For You 筛选条件收敛成稳定签名，避免数组引用变化导致 session_id 误刷新
  const forYouFilterSignature = useMemo(
    () =>
      JSON.stringify({
        sex,
        nsfw,
        selectedTagIds,
        selectedContentTypes,
      }),
    [sex, nsfw, selectedTagIds, selectedContentTypes]
  )
  const prevForYouFilterSignatureRef = useRef(forYouFilterSignature)

  // 监听页面可见性变化，从其他页面切回时刷新 session_id
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && sorting === 'For You') {
        setForYouSessionId(uuid())
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [sorting])

  // 筛选态是否已从 localStorage 回读完成。首屏列表请求以此为闸：
  // 回读前 SWR key 为 null 不发请求，回读后一次性用真实筛选值发出，
  // 消除「默认 key 先发一次 → effect 改 atom → key 变化 → 重发」的双拉
  // （双拉浪费一整屏媒体下载，且与 SW 预取 boot 读的 localStorage 值失配导致预取 miss）。
  // 注意不能改成 atom effect 里同步 setSelf 回读：sorting/sex 参与 SSR 首帧渲染
  // （桌面 sorting 按钮选中态、移动端性别 Select），首帧与 SSR HTML 不一致会触发
  // hydration mismatch，整棵 Suspense 树降级客户端重渲染，代价远大于双拉。
  const [filtersHydrated, setFiltersHydrated] = useState(false)

  useEffect(() => {
    // 归一化与 boot 预取（packages/sw-prefetch HomePlugin）同调一份 normalizeHomeSorting：
    // 旧值/缺失归为 'For You'，避免两侧 sorting 失配导致预取 miss。
    const normalizedSorting = normalizeHomeSorting(
      localStorage.getItem('homeSortingState')
    ) as HomeSorting
    setSorting(normalizedSorting)
    localStorage.setItem('homeSortingState', normalizedSorting)
    const localHomeGender = localStorage.getItem('homeGenderState')
    if (localHomeGender !== null) {
      setSex(localHomeGender as HomeGenderState)
    }
    const localFeaturedTimeState = localStorage.getItem('homeFeaturedTimeState')
    if (localFeaturedTimeState) {
      setFeaturedTime(localFeaturedTimeState as HomeFeaturedTime)
    }
    markPerformanceStage(HOME_PERFORMANCE_MARKS.filtersHydrated)
    setFiltersHydrated(true)
  }, [setFeaturedTime, setSex, setSorting])

  const sortingType = useMemo<HomeSortingType>(() => {
    return sorting === 'For You'
      ? 'For You'
      : sorting === 'World'
        ? 'World'
        : sorting === 'Multi-character'
          ? 'Multi-character'
          : sorting === 'Following'
            ? 'Following'
            : 'Default'
  }, [sorting])

  // Default: 支持不同排序、周期和 tag 筛选，也支持性别、Nsfw 筛选
  const defaultReq: Omit<HomeCharacterListReq, 'page'> = useMemo(() => {
    // gender / sorting 映射走共享纯函数（constant/prefetch-home-sorting.ts），
    // 与预取 boot 同调一份逻辑，避免人工复刻漂移。
    const mappedGender = mapGenderToApi(sex)
    const genderOption: { gender?: Gender } = mappedGender
      ? { gender: mappedGender }
      : {}

    const selectedContentType =
      selectedContentTypes.length === 1 ? selectedContentTypes[0] : undefined

    return {
      // 无明确映射的 sorting（如 Popular/New/World）原样透传，保留既有行为。
      sorting: mapHomeSortingToApi(sorting, featuredTime) ?? sorting,
      size: LIMIT,
      // Following暂时不支持标签筛选，所以传空数组
      tag_ids: sorting === 'Following' ? [] : selectedTagIds,
      ...(selectedContentType ? { content_type: selectedContentType } : {}),
      ...genderOption,
      nsfw,
      language_code: lang,
    }
  }, [
    sex,
    featuredTime,
    sorting,
    LIMIT,
    selectedTagIds,
    selectedContentTypes,
    nsfw,
    lang,
  ])
  const defaultGetKey = useMemo(
    () => getCharacterListPublicKey(defaultReq),
    [defaultReq]
  )
  const defaultFetcher = useMemo(
    () =>
      async ({ req }: { req: HomeCharacterListReq }) => {
        const list = await runWithPerformanceStages(
          HOME_PERFORMANCE_MARKS.listRequestStart,
          HOME_PERFORMANCE_MARKS.listRequestEnd,
          () => getCharacterListPublic(req)
        )
        persistFirstPage(DEFAULT_LIST_ENDPOINT, req, list)
        return list
      },
    [persistFirstPage]
  )

  // For You 列表参数收集
  const forYouReq: Omit<HomeCharacterForYouReq, 'page'> = useMemo(() => {
    // gender 映射走共享纯函数（同 defaultReq），与预取 boot 同调一份逻辑，避免漂移。
    const mappedGender = mapGenderToApi(sex)
    const genderOption: { gender?: Gender } = mappedGender
      ? { gender: mappedGender }
      : {}
    const selectedContentType =
      selectedContentTypes.length === 1 ? selectedContentTypes[0] : undefined

    return {
      size: LIMIT,
      ...genderOption,
      ...(selectedContentType ? { content_type: selectedContentType } : {}),
      nsfw,
      language_code: lang,
      tag_ids: selectedTagIds,
      session_id: forYouSessionId,
    }
  }, [
    sex,
    LIMIT,
    nsfw,
    lang,
    selectedTagIds,
    selectedContentTypes,
    forYouSessionId,
  ])

  // 监听 For You 筛选条件变化，重新生成 session_id
  // hydratedBaselineRef：filtersHydrated 后的首次运行只把回读后的筛选值记为基线、
  // 不刷新 session_id——否则「localStorage 回读改了 sex」会被误判成用户改筛选，
  // 多生成一次 session_id → SWR key 二跳 → 首屏 For You 双拉（正是本次要消除的）。
  const hydratedBaselineRef = useRef(false)
  useEffect(() => {
    if (!filtersHydrated) return
    if (!hydratedBaselineRef.current) {
      hydratedBaselineRef.current = true
      prevSortingRef.current = sorting
      prevForYouFilterSignatureRef.current = forYouFilterSignature
      return
    }
    const prevSorting = prevSortingRef.current
    const prevFilterSignature = prevForYouFilterSignatureRef.current

    prevSortingRef.current = sorting
    prevForYouFilterSignatureRef.current = forYouFilterSignature

    // 只有进入 For You 或筛选条件真实变化时才刷新，减少列表重拉和卡片重新挂载
    if (
      sorting === 'For You' &&
      (prevSorting !== 'For You' ||
        prevFilterSignature !== forYouFilterSignature)
    ) {
      setForYouSessionId(uuid())
    }
  }, [filtersHydrated, forYouFilterSignature, sorting])
  const forYouGetKey = useMemo(
    () => getCharacterForYouListPublicKey(forYouReq),
    [forYouReq]
  )
  const forYouFetcher = useMemo(
    () =>
      async ({ req }: { req: HomeCharacterForYouReq }) => {
        const list = await runWithPerformanceStages(
          HOME_PERFORMANCE_MARKS.listRequestStart,
          HOME_PERFORMANCE_MARKS.listRequestEnd,
          () => getCharacterForYouListPublic(req)
        )
        persistFirstPage(FOR_YOU_LIST_ENDPOINT, req, list)
        return list
      },
    [persistFirstPage]
  )

  const storyReq: Omit<HomeStoryReq, 'page'> = useMemo(() => {
    return {
      size: LIMIT,
      nsfw,
      language_code: lang,
    }
  }, [LIMIT, nsfw, lang])
  const storyGetKey = useMemo(() => getStoryListKey(storyReq), [storyReq])
  const storyFetcher = useMemo(
    () =>
      async ({ req }: { req: HomeStoryReq }) => {
        const list = await runWithPerformanceStages(
          HOME_PERFORMANCE_MARKS.listRequestStart,
          HOME_PERFORMANCE_MARKS.listRequestEnd,
          () => getStoryList(req, lang)
        )
        persistFirstPage(STORY_LIST_ENDPOINT, req, list)
        return list
      },
    [lang, persistFirstPage]
  )
  const gameReq = useMemo(
    () => ({
      language_code: lang || 'en',
      nsfw,
      size: LIMIT,
    }),
    [LIMIT, lang, nsfw]
  )
  const gameGetKey = useMemo(() => {
    return (page: number, previousPageData?: SimulatorGameHomeCard[]) => {
      if (previousPageData && previousPageData.length === 0) return null
      return getSimulatorGameListKey({
        ...gameReq,
        page,
      })
    }
  }, [gameReq])
  const gameFetcher = useMemo(
    () =>
      async ({ req }: { req: SimulatorGameListReq }) => {
        const data = await runWithPerformanceStages(
          HOME_PERFORMANCE_MARKS.listRequestStart,
          HOME_PERFORMANCE_MARKS.listRequestEnd,
          () => getSimulatorGameList(req)
        )
        const list = (data.items ?? []).map(adaptSimulatorGameToHomeCard)
        persistFirstPage(simulatorGameListUrl, req, list)
        return list
      },
    [persistFirstPage]
  )

  const getKeyBuilder = useMemo(() => {
    switch (sortingType) {
      case 'For You':
        return forYouGetKey
      case 'World':
        return gameGetKey
      case 'Multi-character':
        return storyGetKey
      default:
        return defaultGetKey
    }
  }, [defaultGetKey, forYouGetKey, gameGetKey, storyGetKey, sortingType])
  const fetcher = useMemo(() => {
    switch (sortingType) {
      case 'For You':
        return forYouFetcher
      case 'World':
        return gameFetcher
      case 'Multi-character':
        return storyFetcher
      default:
        return defaultFetcher
    }
  }, [defaultFetcher, forYouFetcher, gameFetcher, storyFetcher, sortingType])

  const firstPageCacheMatch = useMemo(() => {
    if (!filtersHydrated) return undefined
    const firstPageKey = getKeyBuilder(
      0,
      undefined as never
    ) as HomeFirstPageKey | null
    if (!firstPageKey || !firstPageKey.req) return undefined
    const endpoint = resolveHomeListEndpoint(firstPageKey)
    if (!endpoint) return undefined
    return createHomeFirstScreenCacheMatch({
      endpoint,
      body: firstPageKey.req,
      userId,
      device,
    })
  }, [device, filtersHydrated, getKeyBuilder, userId])
  activeCacheMatchRef.current = firstPageCacheMatch

  const cachedFirstPage = useMemo(() => {
    if (!firstPageCacheMatch) return undefined
    return readHomeFirstScreenCache<
      | CharacterGetRes
      | StoryGetRes
      | CharacterGetResWithStory
      | SimulatorGameHomeCard
    >(firstPageCacheMatch)
  }, [firstPageCacheMatch])
  const cachedFirstPageItems = useMemo(
    () =>
      selectHomeFirstScreenCacheItems(
        cachedFirstPage?.items ?? [],
        lockedCacheItemCount
      ),
    [cachedFirstPage, lockedCacheItemCount]
  )
  const homeFirstScreenCacheHit = cachedFirstPageItems.length > 0

  const {
    data: characterListPages,
    isValidating: validatingInPages,
    error: inPagesError,
    size,
    setSize,
  } = useSWRInfinite<HomeListPage>(
    // filtersHydrated 前返回 null 挂起请求，等 localStorage 筛选态回读完成后
    // 一次性用真实 key 发出（见上方 filtersHydrated 注释，消除首屏双拉）。
    (pageIndex, previousPageData) =>
      filtersHydrated
        ? getKeyBuilder(pageIndex, previousPageData as never)
        : null,
    fetcher,
    {
      keepPreviousData: false,
      fallbackData: homeFirstScreenCacheHit
        ? [cachedFirstPageItems as HomeListPage]
        : undefined,
      // fallbackData 只负责立即回显；每次挂载仍请求第一页刷新本地快照，避免把
      // 7 天快照 TTL 误解为服务端数据的新鲜度。
      revalidateOnMount: true,
      initialSize: initialPageCount,
      parallel: initialPageCount > 1,
      // useSWRInfinite 默认 revalidateFirstPage:true——每次 setSize 翻新页都会顺带
      // 重新请求第一页(page=0)检测头部更新。首页「加载更多」不需要这个，关掉以免
      // 每翻一页都多打一次 page=0 请求。
      revalidateFirstPage: false,
    }
  )
  const characterGetResList = useMemo(() => {
    if (sortingType === 'For You') {
      const flatList = characterListPages?.flat() ?? []
      return mergeHomeFirstScreenCacheItems(
        cachedFirstPageItems as CharacterGetResWithStory[],
        flatList as CharacterGetResWithStory[],
        getForYouItemIdentity
      )
    }
    if (sortingType === 'World') {
      return mergeHomeFirstScreenCacheItems(
        cachedFirstPageItems as SimulatorGameHomeCard[],
        (characterListPages?.flat() || []) as SimulatorGameHomeCard[],
        (item) => item.game_id
      )
    }
    if (sortingType === 'Multi-character') {
      return mergeHomeFirstScreenCacheItems(
        cachedFirstPageItems as StoryGetRes[],
        (characterListPages?.flat() || []) as StoryGetRes[],
        (item) => item.story_id
      )
    }
    return mergeHomeFirstScreenCacheItems(
      cachedFirstPageItems as CharacterGetRes[],
      (characterListPages?.flat() || []) as CharacterGetRes[],
      (item) => item.character.character_id
    )
  }, [cachedFirstPageItems, characterListPages, sortingType])

  // 触底加载下一页。末尾检测已交给渲染层（VirtualHomeGrid 用虚拟器的「最后可视行」
  // index 判断，见 onReachEnd），这里只做「能不能再翻」的守卫：
  //   - 正在请求(validatingInPages) → 跳过：天然挡住「一次下滑连拉好几页」，无需
  //     原来那套加载闸/上升沿/rAF 补丁（虚拟器 index 不受新行尚未进 DOM 影响）。
  //   - 出错 / 已到末页（最后一页空）→ 跳过。
  // useMemoizedFn 返回稳定引用且每次读最新值，可安全作为高频回调传给渲染层。
  const loadMore = useMemoizedFn(() => {
    if (validatingInPages || inPagesError) return
    if ((characterListPages?.[size - 1]?.length ?? 0) === 0) return
    setSize((s) => s + 1).then()
  })

  useEffect(() => {
    if (inPagesError) {
      console.error('useSWRInfinite Error:', inPagesError)
      message.error(inPagesError.message)
    }
  }, [inPagesError])

  const resetTotalPages = useMemoizedFn(() => {
    setSize(initialPageCount).then()
  })
  const handleSelect = useMemoizedFn((value: HomeSorting) => {
    if (value === sorting) return
    resetTotalPages()
    setSorting(value)
  })
  const handleNsfwChange = useMemoizedFn((checked: boolean) => {
    updateNsfw(checked)
    resetTotalPages()

    sendLog({
      name: 'Home',
      event: 'nsfw_change',
      source: location.pathname,
      additionalData: {
        userId: user?.user_id,
        nsfw: checked,
      },
    })
  })
  const handleSexChange = useMemoizedFn((value: string) => {
    if (value === sex) return
    resetTotalPages()
    setSex(value as HomeGenderState)
  })
  const handleTimeChange = useMemoizedFn((value: string) => {
    if (value === time) return
    resetTotalPages()
    setTime(value as HomePopularTime)
  })
  const handleFeaturedTimeChange = useMemoizedFn((value: string) => {
    if (value === featuredTime) return
    resetTotalPages()
    setFeaturedTime(value as HomeFeaturedTime)
  })

  // 是否还会有下一页：只在「正在加载下一页」时为 true，让渲染层把末尾不满一行的
  // cell 暂时裁掉、等这页数据补齐成整行再显示。加载结束后一律 false → 末行照常显示，
  // 绝不会把到底时合法的最后一行永久藏起来（避免丢卡片）。
  // 注意：不用 lastPageLen>=LIMIT 判断「还有更多」——LIMIT 随测量/screenBuffer 变化、
  // 后端每页实际条数也未必等于 LIMIT，会误判。
  const hasMore = validatingInPages

  return {
    sortingType,
    resetTotalPages,
    handleSelect,
    handleNsfwChange,
    handleSexChange,
    handleTimeChange,
    handleFeaturedTimeChange,
    characterListPages,
    characterGetResList,
    homeFirstScreenCacheHit,
    validatingInPages,
    hasMore,
    loadMore,
  }
}

export default useHomeCharacterList
